/**
 * Ready-to-ship — response contract for
 * `POST /api/seller-orders/[id]/ready-to-ship`.
 *
 * One-click seller action (PLAN §L.6 Q2 — no confirm step): seller hits the
 * endpoint, the server re-rates against current Shippo prices, applies the
 * §I.2 delta-cap, and either (a) buys the label synchronously, (b) responds
 * with an operator-escalate signal (delta 5–20%), or (c) responds with a
 * refund signal (delta >20%). The route never refunds itself — the refund
 * surface is the operator's runbook for now (REFUND_RUNBOOK §3.1).
 *
 * The response is a discriminated union — `kind` is the variant axis. Each
 * variant carries only the fields it owns (Make Illegal States Unrepresentable).
 *
 * Idempotency (PLAN §L.6 Q4): if the row already has a purchased label, the
 * route short-circuits to `purchased` with the existing label fields and
 * **does not** re-rate or re-buy.
 *
 * @see .tondocs/shippo/PLAN.md §L.1 — decision
 * @see .tondocs/shippo/PLAN.md §L.6 — resolved policies (one-click, idempotency)
 * @see .tondocs/shippo/PLAN.md §I.2 — delta-cap thresholds
 * @see .tondocs/skills/api-contract-design.md §A1 — error contract
 * @see .tondocs/skills/type-driven-design.md §1 §3 — ADT + exhaustiveness
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Discriminator tuple — exhaustive list of every response `kind`
// ---------------------------------------------------------------------------

/**
 * Same pattern as `BLOCKER_KINDS` / `REFUND_STATUSES` — the array↔union pin
 * below catches drift (extending one side without the other) at `tsc`.
 */
export const READY_TO_SHIP_RESPONSE_KINDS = [
  "purchased",
  "rate_delta_escalate",
  "rate_delta_refund",
] as const;

export type ReadyToShipResponseKind =
  (typeof READY_TO_SHIP_RESPONSE_KINDS)[number];

// ---------------------------------------------------------------------------
// Success variant — label bought (or idempotency short-circuit)
// ---------------------------------------------------------------------------

/**
 * The label is purchased and the seller can print it. `labelCost.amount` is
 * what Shippo actually billed Kraahl (vendor-side money — distinct from the
 * buyer-paid estimate, see PLAN §O.1.6 SoT-divergence pair). Currency is the
 * row's `seller_orders.label_cost_currency` (always present; defaults USD).
 */
export interface ReadyToShipPurchasedResponse {
  kind: "purchased";
  labelUrl: string;
  trackingNumber: string;
  carrier: string;
  servicelevel: string;
  labelCost: { amount: number; currency: string };
  labelStatus: "purchased";
  /** ISO-8601 UTC, when the label was bought (idempotent re-clicks return the
   *  original timestamp, not the re-click time). */
  purchasedAt: string;
}

// ---------------------------------------------------------------------------
// Delta-cap variants — 409 responses
// ---------------------------------------------------------------------------

/**
 * Fresh rate is 5–20% above the checkout estimate. Route does NOT buy — the
 * decision goes to the operator (PLAN §I.2). UI surfaces "Shipping costs
 * have changed, our team will reach out".
 */
export interface ReadyToShipRateDeltaEscalateResponse {
  kind: "rate_delta_escalate";
  /** Major-units numbers in the same currency (label_cost_currency / row currency). */
  estimate: { amount: number; currency: string };
  current: { amount: number; currency: string };
  /** Signed fraction (e.g. 0.12 for +12%). Always > 0.05 and ≤ 0.20 here. */
  deltaFraction: number;
}

/**
 * Fresh rate is >20% above the estimate. Route does NOT buy and signals
 * "refund" — actual refund is operator-driven for now (REFUND_RUNBOOK §3.1).
 */
export interface ReadyToShipRateDeltaRefundResponse {
  kind: "rate_delta_refund";
  estimate: { amount: number; currency: string };
  current: { amount: number; currency: string };
  /** Signed fraction (> 0.20 here). */
  deltaFraction: number;
}

// ---------------------------------------------------------------------------
// Union — discriminated by `kind`
// ---------------------------------------------------------------------------

export type ReadyToShipResponse =
  | ReadyToShipPurchasedResponse
  | ReadyToShipRateDeltaEscalateResponse
  | ReadyToShipRateDeltaRefundResponse;

// Compile-time exhaustiveness pin — array↔union
type _AssertReadyToShipKindsCoverUnion =
  (typeof READY_TO_SHIP_RESPONSE_KINDS)[number] extends ReadyToShipResponse["kind"]
    ? true
    : never;
type _AssertReadyToShipNoExtraKinds =
  ReadyToShipResponse["kind"] extends (typeof READY_TO_SHIP_RESPONSE_KINDS)[number]
    ? true
    : never;

const _pinReadyToShipKindsCoverUnion: _AssertReadyToShipKindsCoverUnion = true;
const _pinReadyToShipNoExtraKinds: _AssertReadyToShipNoExtraKinds = true;
void _pinReadyToShipKindsCoverUnion;
void _pinReadyToShipNoExtraKinds;

// ---------------------------------------------------------------------------
// Zod schemas — pinned to interfaces
// ---------------------------------------------------------------------------

const MoneyShape = z.object({
  amount: z.number(),
  currency: z.string().min(3),
});

export const ReadyToShipPurchasedResponseSchema =
  pinned<ReadyToShipPurchasedResponse>()(
    z.object({
      kind: z.literal("purchased"),
      labelUrl: z.string().min(1),
      trackingNumber: z.string().min(1),
      carrier: z.string().min(1),
      servicelevel: z.string().min(1),
      labelCost: MoneyShape,
      labelStatus: z.literal("purchased"),
      purchasedAt: z.string().min(1),
    }),
  );

export const ReadyToShipRateDeltaEscalateResponseSchema =
  pinned<ReadyToShipRateDeltaEscalateResponse>()(
    z.object({
      kind: z.literal("rate_delta_escalate"),
      estimate: MoneyShape,
      current: MoneyShape,
      deltaFraction: z.number(),
    }),
  );

export const ReadyToShipRateDeltaRefundResponseSchema =
  pinned<ReadyToShipRateDeltaRefundResponse>()(
    z.object({
      kind: z.literal("rate_delta_refund"),
      estimate: MoneyShape,
      current: MoneyShape,
      deltaFraction: z.number(),
    }),
  );

export const ReadyToShipResponseSchema = pinned<ReadyToShipResponse>()(
  z.discriminatedUnion("kind", [
    ReadyToShipPurchasedResponseSchema,
    ReadyToShipRateDeltaEscalateResponseSchema,
    ReadyToShipRateDeltaRefundResponseSchema,
  ]),
);

// ---------------------------------------------------------------------------
// Error codes — surfaced in the legacy `{ error, code }` body (apiLegacyError)
// ---------------------------------------------------------------------------

/**
 * Stable wire-level error codes the legacy `{ error, code }` body can carry on
 * a NON-union response (anything that is NOT the 200/409 discriminated union).
 *
 * UI / mobile pattern-match on `code`, never on `error` (human-readable, not
 * stable). HTTP status mapping in the route:
 *  - 401 UNAUTHORIZED                         — no session
 *  - 403 FORBIDDEN                            — caller is not the seller
 *  - 404 ORDER_NOT_FOUND                      — seller_order id does not exist
 *  - 409 SHIPPO_READY_TO_SHIP_IN_PROGRESS     — single-flight claim already held
 *  - 422 SHIPPING_SANCTIONED_DESTINATION      — OFAC gate at re-rate time
 *  - 422 SHIPPO_NO_RATES_TO_DESTINATION       — Shippo returned 0 rates
 *  - 422 SHIPPO_RATE_MISSING                  — row lacks persisted rate inputs
 *      / corrupted customs / address snapshot
 *  - 502 SHIPPO_CARRIER_REJECTED              — Shippo returned 200 + status="ERROR"
 *      (carrier refused the buy; no billable transaction created — SAFE TO RETRY)
 *  - 502 SHIPPO_UPSTREAM_ERROR                — Shippo SDK THREW (network, 5xx,
 *      truly ambiguous — DOUBLE-CHARGE RISK on auto-retry)
 *
 * The CARRIER_REJECTED ↔ UPSTREAM_ERROR distinction matters: probe 2026-06-30
 * confirmed that Shippo returns HTTP 200 with `status:"ERROR"` when a carrier
 * (USPS / DHL / etc.) refuses the buy at its own backend. In that case Shippo
 * has NOT billed us — the route can safely revert the single-flight claim to
 * NULL and let the seller retry. Throws, by contrast, are ambiguous: Shippo
 * MAY have created a billable transaction we never saw, so the row STAYS in
 * `label_status='failed'` and recovery is operator-only. See the route's
 * step 8 split and `tests/integration/seller-orders-ready-to-ship.test.ts`.
 *
 * Note: 409 also has TWO variants that travel on the DISCRIMINATED-UNION body
 * (`rate_delta_escalate`, `rate_delta_refund` — see {@link ReadyToShipResponse}),
 * NOT on this legacy `{error, code}` shape. The client distinguishes them by
 * inspecting `body.kind` before classifying. `SHIPPO_READY_TO_SHIP_IN_PROGRESS`
 * was the regression on 2026-06-30: the client classified ANY 409 as union,
 * which silently 500'd the seller dashboard when single-flight rejected a
 * concurrent click. See `tests/unit/fitness-ready-to-ship-codes-aligned.test.ts`
 * for the route↔contract alignment pin that prevents the next drift.
 */
export const READY_TO_SHIP_ERROR_CODES = [
  "UNAUTHORIZED",
  "FORBIDDEN",
  "ORDER_NOT_FOUND",
  // Order is in a terminal / non-shippable status (cancelled / refunded /
  // delivered / already shipped) — a fresh label buy is refused BEFORE the
  // single-flight claim. Scar 2026-07-11 (cancelled live-rate order still
  // offered "Ready to ship"). HTTP 409.
  "ORDER_NOT_SHIPPABLE",
  "SHIPPO_READY_TO_SHIP_IN_PROGRESS",
  "SHIPPING_SANCTIONED_DESTINATION",
  "SHIPPO_NO_RATES_TO_DESTINATION",
  "SHIPPO_RATE_MISSING",
  "SHIPPO_CARRIER_REJECTED",
  "SHIPPO_UPSTREAM_ERROR",
] as const;

export type ReadyToShipErrorCode = (typeof READY_TO_SHIP_ERROR_CODES)[number];
